import { usePageHeader } from "@hooks/use_page_header";
import { useState } from "react";
import { Button, Select, Tabs, TabsProps } from "antd";
import { useQuery } from "@tanstack/react-query";
import { HomeService } from "@services/home.service";
import { useStudentBio } from "@hooks/use_student_bio";
import { Spiner } from "@components/loaders/spiner";
import { toRp } from "@utils/helper";
import moment from "moment";
import {
  CaretDownFilled,
  CaretUpFilled,
  FilterOutlined,
} from "@ant-design/icons";
import { PopupCustom } from "@components/general/popup";
import { LuArrowDownLeft, LuArrowUpRight, LuWallet } from "react-icons/lu";

export const PocketInfo = () => {
  usePageHeader("Uang Saku", { bottomNavigation: "hidden" });

  const [popupFilter, setPopupFilter] = useState<boolean>(false);
  const [limit, setLimit] = useState("5");
  const [openDetails, setOpenDetails] = useState<number[]>([]);

  const { nis } = useStudentBio();

  const { data: dataSaldoUangSaku, isPending: isPendingUangSaku } = useQuery({
    queryKey: [HomeService.queries.GET_BILLS_AND_BALANCES, nis],
    queryFn: () => HomeService.billsAndBalances({ nis }),
  });

  const { data: laporanUangSaku, isPending: isPendingLaporanUangSaku } =
    useQuery({
      queryKey: [HomeService.queries.GET_DATA_REPORT_BALANCES, nis, limit],
      queryFn: () => HomeService.reportBalances({ nis, limit }),
    });

  const toggleDetail = (idx: number) => {
    setOpenDetails((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx],
    );
  };


  const TransactionRow = ({
    tanggal,
    catatan,
    amount,
    tipe,
    detail,
    jenis,
    idx,
  }: {
    tanggal: string | Date;
    catatan: string;
    amount: number;
    tipe: "masuk" | "keluar";
    detail?: { keterangan?: string; nominal?: string; tagihan?: string; dibayar?: string }[];
    jenis?: string;
    idx: number;
  }) => (
    <div className="tw-py-3 tw-border-b tw-border-background-300 last:tw-border-0">
      <div className="tw-flex tw-justify-between tw-items-center">
        <div className="tw-flex tw-items-center tw-gap-2.5">
          <div
            className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-xl tw-shrink-0"
            style={{ background: tipe === "masuk" ? "#f0fdf4" : "#fef2f2" }}
          >
            {tipe === "masuk" ? (
              <LuArrowDownLeft className="tw-text-sm tw-text-emerald-500" />
            ) : (
              <LuArrowUpRight className="tw-text-sm tw-text-red-400" />
            )}
          </div>
          <div>
            <div className="tw-text-sm tw-font-semibold tw-text-text-primary tw-max-w-[180px] tw-truncate">
              {catatan || "-"}
            </div>
            <div className="tw-text-[10px] tw-text-text-tertiary">
              {moment(tanggal ?? Date.now()).format("DD MMM YYYY")}
            </div>
          </div>
        </div>
        <div
          className={`tw-text-sm tw-font-bold ${
            tipe === "masuk" ? "tw-text-emerald-600" : "tw-text-red-500"
          }`}
        >
          {tipe === "masuk" ? "+" : "-"}{toRp(amount)}
        </div>
      </div>

      {detail && detail.length > 0 && jenis !== "" && (
        <>
          <div
            className="tw-mt-2 tw-cursor-pointer tw-flex tw-items-center tw-justify-between tw-gap-1 tw-text-xs tw-text-primary"
            onClick={() => toggleDetail(idx)}
          >
            <span>Lihat detail ({detail.length})</span>
            {openDetails.includes(idx) ? <CaretUpFilled /> : <CaretDownFilled />}
          </div>
          {openDetails.includes(idx) && (
            <div className="tw-mt-2 tw-rounded-xl tw-bg-background-200 tw-p-2 tw-space-y-1.5">
              {detail.map((d, dIdx) => (
                <div key={dIdx} className="tw-flex tw-justify-between tw-text-xs tw-text-text-secondary">
                  {(jenis ?? "").toLowerCase() === "ziswaf" ? (
                    <>
                      <span className="tw-flex-1">{d.keterangan}</span>
                      <span>{toRp(parseFloat((d.nominal ?? "0").replace(",", "")))}</span>
                    </>
                  ) : (
                    <>
                      <span className="tw-flex-1">{d.tagihan}</span>
                      <span>{toRp(parseFloat((d.dibayar ?? "0").replace(",", "")))}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  const masukList = (laporanUangSaku?.data ?? [])
    .sort((a, b) => moment(b.tanggal).diff(moment(a.tanggal)))
    .filter((i) => i.tipe === "masuk")
    .slice(0, parseInt(limit));

  const keluarList = (laporanUangSaku?.data ?? [])
    .sort((a, b) => moment(b.tanggal).diff(moment(a.tanggal)))
    .filter((i) => i.tipe === "keluar")
    .slice(0, parseInt(limit));

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Masuk",
      children: (
        <div className="tw-px-3 tw-pb-3">
          {masukList.length === 0 ? (
            <div className="tw-py-8 tw-text-center tw-text-sm tw-text-text-tertiary">
              Tidak ada data
            </div>
          ) : (
            masukList.map((item, idx) => (
              <TransactionRow
                key={idx}
                idx={idx}
                tanggal={item.tanggal}
                catatan={item.catatan}
                amount={parseFloat(((item.uangsakumasuk ?? "") + "").replaceAll(",", "") || "0")}
                tipe="masuk"
              />
            ))
          )}
        </div>
      ),
    },
    {
      key: "2",
      label: "Keluar",
      children: (
        <div className="tw-px-3 tw-pb-3">
          {keluarList.length === 0 ? (
            <div className="tw-py-8 tw-text-center tw-text-sm tw-text-text-tertiary">
              Tidak ada data
            </div>
          ) : (
            keluarList.map((item, idx) => (
              <TransactionRow
                key={idx}
                idx={idx}
                tanggal={item.tanggal}
                catatan={item.catatan}
                amount={parseFloat(((item.uangkeluar ?? "") + "").replaceAll(",", "") || "0")}
                tipe="keluar"
                detail={item.detail}
                jenis={item.jenis}
              />
            ))
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="tw-pb-20">
        {/* Compact Green Header */}
        <div
          className="tw-relative tw-overflow-hidden tw-px-5 tw-pt-5 tw-pb-6"
          style={{ background: "linear-gradient(155deg, #052e16 0%, #166534 70%)" }}
        >
          <div className="tw-absolute tw-rounded-full tw-pointer-events-none"
            style={{ background: "#eab308", opacity: 0.08, width: 140, height: 140, top: -50, right: -25 }} />
          <div className="tw-absolute tw-rounded-full tw-pointer-events-none"
            style={{ background: "#ffffff", opacity: 0.05, width: 70, height: 70, top: 10, right: 45 }} />

          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-relative tw-z-10">
            <div className="tw-flex tw-items-center tw-gap-1.5">
              <LuWallet size={13} style={{ color: "#86efac" }} />
              <span className="tw-text-xs tw-font-medium" style={{ color: "#86efac" }}>
                Uang Saku Tersisa
              </span>
            </div>
            <FilterOutlined
              className="tw-cursor-pointer"
              style={{ color: "rgba(255,255,255,0.65)", fontSize: 16 }}
              onClick={() => setPopupFilter(true)}
            />
          </div>
          <div className="tw-text-[26px] tw-font-bold tw-text-white tw-leading-tight tw-relative tw-z-10">
            {isPendingUangSaku ? <Spiner /> : toRp(dataSaldoUangSaku?.data?.saldo ?? 0)}
          </div>
        </div>

        {/* Transactions */}
        <div className="tw-px-4 tw-mt-3">
          <div className="tw-rounded-2xl tw-bg-white tw-overflow-hidden"
            style={{ boxShadow: "0 2px 12px rgba(22,101,52,0.08)", border: "1px solid #f0fdf4" }}>
            {isPendingLaporanUangSaku ? (
              <div className="tw-py-12 tw-text-center">
                <Spiner />
              </div>
            ) : (
              <Tabs defaultActiveKey="1" items={items} className="pocket-tabs" centered />
            )}
          </div>
        </div>
      </div>

      <PopupCustom
        title="Filter Data"
        open={popupFilter}
        onCancel={() => setPopupFilter(false)}
        slideUpModal={true}
      >
        <Select
          className="tw-text-base"
          placeholder="Pilih Jumlah Ditampilkan"
          defaultValue={limit}
          style={{ width: "100%" }}
          allowClear
          onChange={(e) => setLimit(e)}
          options={[
            { value: "5", label: "5 data terakhir" },
            { value: "10", label: "10 data terakhir" },
            { value: "25", label: "25 data terakhir" },
          ]}
        />
        <Button
          onClick={() => setPopupFilter(false)}
          className="tw-p-2 tw-mt-6 tw-h-10 tw-w-full tw-text-sm tw-rounded-xl tw-border-0 tw-text-white focus:tw-shadow-none"
          style={{ background: "#ef4444" }}
        >
          Tutup
        </Button>
      </PopupCustom>
    </>
  );
};
